"use client"

import * as React from "react"
import { Check, ChevronDown, X, Search, ListChecks } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useSearchMode, fuzzyMatch, type SearchMode } from "./use-search-mode"
import { VIEW_FILTER_ALL, isAllSelected } from "@/lib/view-preferences/filter-sentinel"
import { useShellTranslation, type ShellTranslate } from "@/lib/shell/use-shell-translation"
import { richText } from "@/lib/i18n/rich-text"

/** Above this count, the popover auto-opens in "Selected only" review mode. */
const LARGE_SELECTION_THRESHOLD = 50

/**
 * Up to this many options the list shows every one, with no scroll box.
 *
 * #361 — the options used to sit in a 260px box that fits "Select All" plus
 * about six rows. The campaigns Status facet has eight, so Cancelled and Error
 * were below the fold, and a macOS overlay scrollbar gave no cue. The tester
 * reported the status as missing. An enum facet is short; it should never
 * need scrolling.
 */
const SHORT_LIST_MAX = 10

export interface FilterOption {
  value: string
  label: string
  /** Count badge shown next to label (e.g. "(12)"). When 0, option is disabled with tooltip. */
  count?: number
  /** Explicit disabled flag — takes precedence over count check */
  disabled?: boolean
}

interface MultiSelectFilterProps {
  title: string
  options: FilterOption[]
  selectedValues: string[]
  onSelectionChange: (values: string[]) => void
  /** Show search input when option count exceeds this threshold. Default: 0 (always show) */
  searchThreshold?: number
  /** Tooltip description shown on hover when no values are selected */
  description?: string
  /** Hide the Exact/Fuzzy search mode toggle (e.g. for enum filters where it is meaningless) */
  hideSearchMode?: boolean
  /**
   * Extra classes for the trigger button — e.g. `h-8 text-xs` to sit in a
   * row of compact chips (Feedback Desk quick filters) instead of the
   * table toolbar's h-9 default.
   */
  triggerClassName?: string
  /**
   * One value at a time (a scope such as Company / Workspace on the
   * super-admin Feedback Desk). Same chip, same popover, so a single-value
   * facet does not look like a form field next to the multi-value ones:
   * picking an option replaces the selection and closes the popover,
   * picking it again clears it, there is no "Select All" row, and the row
   * shows a check mark instead of a checkbox.
   */
  singleSelect?: boolean
  /**
   * Hover hint on an option that cannot be picked (count 0 or disabled). Name
   * the table's own rows ("No campaigns in this status"). #361: every table
   * used to say "No recipients in this status". Without one, the chip says
   * "Nothing matches this value right now" in the interface language.
   */
  emptyOptionHint?: string
}

export function MultiSelectFilter({
  title,
  options,
  selectedValues,
  onSelectionChange,
  searchThreshold = 0,
  description,
  hideSearchMode = false,
  triggerClassName,
  singleSelect = false,
  emptyOptionHint,
}: MultiSelectFilterProps) {
  // The chip's own words speak the interface language (locale audit I-02).
  // `title`, the option labels and `description` come from the page as they are.
  const { t } = useShellTranslation()
  const optionHint = emptyOptionHint ?? t("shell.filter.emptyOptionHint")
  const [open, setOpen] = React.useState(false)
  // PROD-1417 — the chip is both the dropdown's trigger and the summary
  // tooltip's trigger. After Esc, Radix returns focus to the chip (kept: that
  // is the keyboard behaviour), and a focused tooltip trigger opens its
  // tooltip, which then sat where the dropdown had been and read as "Esc did
  // not close it". The tooltip is controlled for its whole life, and the one
  // focus that Radix restores on close is not allowed to open it. Tabbing onto
  // the chip, or hovering it, still does.
  const [tipOpen, setTipOpen] = React.useState(false)
  const suppressFocusTipRef = React.useRef(false)
  const handleTipOpenChange = (next: boolean) => {
    // An open asked for while the dropdown is up is never stored: the tooltip
    // is held shut then, so Radix reports no close when the pointer leaves,
    // and a stored hover would pop the summary up the moment the dropdown
    // closed (Esc, a click outside, or a single-select pick). Radix has
    // already told the provider a tooltip opened before it asks us; that only
    // closes other tooltips and skips the next one's delay, which is harmless.
    if (next && (open || suppressFocusTipRef.current)) return
    setTipOpen(next)
  }
  const [search, setSearch] = React.useState("")
  const [selectedOnly, setSelectedOnly] = React.useState(false)
  const [searchMode, setSearchMode] = useSearchMode()
  // #361 — a long list keeps a scroll box; while more rows sit below the fold
  // it says so, because an overlay scrollbar (macOS) shows nothing at rest.
  const listRef = React.useRef<HTMLDivElement | null>(null)
  const [moreBelow, setMoreBelow] = React.useState(false)
  const measureMoreBelow = React.useCallback(() => {
    const el = listRef.current
    if (!el) return
    setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
  }, [])
  // The popover's content mounts a commit after `open` flips (portal), so the
  // list is measured when it attaches, not only when `open` changes.
  const attachList = React.useCallback(
    (el: HTMLDivElement | null) => {
      listRef.current = el
      if (el) measureMoreBelow()
    },
    [measureMoreBelow],
  )

  const showSearch = options.length > searchThreshold

  // "Select All" collapses the selection to the VIEW_FILTER_ALL sentinel
  // (one token instead of N enumerated values). Everything below treats the
  // sentinel as "every option selected" via `effectiveSelected`.
  const isAll = isAllSelected(selectedValues)
  const allOptionValues = React.useMemo(() => options.map(o => o.value), [options])
  // Mirror the per-option disabled rule used at the row render (count===0 OR
  // explicit disabled). Select All / sentinel-collapse logic must NOT pick
  // up zero-count buckets — otherwise the chip says "Status: All" while
  // half the underlying statuses have no data, and "Select All" appears to
  // toggle options the user couldn't click individually.
  const enabledOptionValues = React.useMemo(
    () =>
      options
        .filter(o => !o.disabled && o.count !== 0)
        .map(o => o.value),
    [options]
  )
  const effectiveSelected = React.useMemo(
    () => (isAll ? new Set(allOptionValues) : new Set(selectedValues)),
    [isAll, allOptionValues, selectedValues]
  )
  const effectiveCount = isAll ? options.length : selectedValues.length
  const isLargeSelection = effectiveCount > LARGE_SELECTION_THRESHOLD

  const selectedLabelList = React.useMemo(
    () =>
      isAll
        ? options.map(o => o.label)
        : selectedValues.map(v => options.find(o => o.value === v)?.label ?? v),
    [isAll, selectedValues, options]
  )

  const useTwoColumns = selectedLabelList.length > 12
  const TOOLTIP_PREVIEW_MAX = 24
  const previewLabels = selectedLabelList.slice(0, TOOLTIP_PREVIEW_MAX)
  const hiddenPreviewCount = selectedLabelList.length - previewLabels.length

  const filtered = React.useMemo(() => {
    let list = options
    if (selectedOnly) {
      list = list.filter(o => effectiveSelected.has(o.value))
    }
    const q = search.trim()
    if (showSearch && q) {
      if (searchMode === "fuzzy") {
        list = list.filter(o => fuzzyMatch(q, o.label))
      } else {
        const tokens = q.toLowerCase().split(/\s+/).filter(Boolean)
        list = list.filter(o => {
          const words = o.label.toLowerCase().split(/[\s\-/,()]+/).filter(Boolean)
          return tokens.every(token => words.some(word => word.startsWith(token)))
        })
      }
    }
    return list
  }, [options, selectedOnly, effectiveSelected, showSearch, search, searchMode])

  // A short list shows every option; only a long one scrolls (#361).
  const scrolls = selectedOnly || filtered.length > SHORT_LIST_MAX

  React.useLayoutEffect(() => {
    if (!open || !scrolls) {
      setMoreBelow(false)
      return
    }
    measureMoreBelow()
  }, [open, scrolls, filtered.length, measureMoreBelow])

  const handleToggle = (value: string) => {
    if (singleSelect) {
      onSelectionChange(selectedValues.includes(value) ? [] : [value])
      setOpen(false)
      setSearch("")
      return
    }
    if (isAll) {
      // Deselecting a value from a full "Select All": expand the sentinel
      // back to the explicit list minus this value. There is no exclude
      // model — once you deselect, the facet is enumerated again.
      onSelectionChange(allOptionValues.filter(v => v !== value))
      return
    }
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter(v => v !== value))
    } else {
      onSelectionChange([...selectedValues, value])
    }
  }

  /**
   * Toggle the "Select All" row. When the resulting selection covers every
   * option, collapse it to the VIEW_FILTER_ALL sentinel so the URL / GET /
   * view-preferences row never enumerate hundreds of values.
   */
  const handleSelectAll = () => {
    // Only act on options that are actually selectable. Zero-count
    // ("Bounced (0)") or explicitly disabled rows would otherwise sneak
    // into the selection here even though the row checkbox is locked.
    const filteredValues = filtered
      .filter(o => !o.disabled && o.count !== 0)
      .map(o => o.value)
    const allFilteredSelected = filteredValues.every(v => effectiveSelected.has(v))
    const base = isAll ? allOptionValues : selectedValues
    if (allFilteredSelected) {
      onSelectionChange(base.filter(v => !filteredValues.includes(v)))
      return
    }
    const merged = new Set([...base, ...filteredValues])
    // Collapse to the "All" sentinel only when every ENABLED option is
    // covered — otherwise the chip would say "Status: All" while two of
    // the four statuses can't even be selected by the user.
    const coversEverything =
      enabledOptionValues.length > 0 && enabledOptionValues.every(v => merged.has(v))
    onSelectionChange(coversEverything ? [VIEW_FILTER_ALL] : Array.from(merged))
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (isOpen) {
      setTipOpen(false)
      setSelectedOnly(isLargeSelection)
    } else {
      setSearch("")
      setSelectedOnly(false)
    }
  }

  // PROD-1365 — the × clears the facet AND keeps the dropdown closed, like the
  // Sent Date chip's × (date-range-picker.tsx). Radix keeps an open popover
  // open when the press lands on its own trigger, so it has to be closed here.
  const clearRef = React.useRef<HTMLSpanElement | null>(null)
  // A press that BEGAN on the × and was released beside it (a 16px target)
  // sends its click to the trigger button, which would toggle the dropdown
  // open and clear nothing. Remember where the press began; recomputed on
  // every press on the chip, so it cannot go stale.
  const clearPressRef = React.useRef(false)

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    clearPressRef.current = false
    onSelectionChange([])
    handleOpenChange(false)
  }

  const handleTriggerPointerDown = (e: React.PointerEvent) => {
    clearPressRef.current = !!clearRef.current?.contains(e.target as Node)
  }

  const handleTriggerClick = (e: React.MouseEvent) => {
    const pressBeganOnClear = clearPressRef.current
    clearPressRef.current = false
    // detail 0 is Enter/Space on the chip: that opens the dropdown as before.
    if (pressBeganOnClear && e.detail !== 0) {
      // Prevented, so Radix's composed trigger handler skips its open toggle.
      e.preventDefault()
      onSelectionChange([])
      handleOpenChange(false)
    }
  }

  return (
    <TooltipProvider>
      <Tooltip
        open={open || selectedValues.length === 0 ? false : tipOpen}
        onOpenChange={handleTipOpenChange}
      >
    <Popover open={open} onOpenChange={handleOpenChange}>
      <TooltipTrigger asChild>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-9 w-auto min-w-fit max-w-[260px] border-dashed", triggerClassName)}
          onPointerDown={handleTriggerPointerDown}
          onClick={handleTriggerClick}
        >
          {selectedValues.length > 0 ? (
            <>
              {/* #658/#659 — show the selected value(s), not just a count.
                  1 picked → "<Title>: <label>"
                  2 picked → "<Title>: <a>, <b>"
                  3+ picked or isAll → "<Title>: <a> +N" / "<Title>: All" */}
              <span className="me-1 shrink-0">{title}:</span>
              <span className="truncate">
                {isAll
                  ? t("shell.filter.all")
                  : selectedLabelList.length === 1
                    ? selectedLabelList[0]
                    : selectedLabelList.length === 2
                      ? selectedLabelList.join(", ")
                      : t("shell.filter.firstAndMore", {
                          first: selectedLabelList[0],
                          count: selectedLabelList.length - 1,
                        })}
              </span>
              <span
                ref={clearRef}
                role="button"
                title={t("shell.filter.clearTitled", { title })}
                onClick={handleClear}
                className="ms-1 shrink-0 rounded-sm opacity-70 hover:opacity-100 hover:bg-muted p-0.5"
              >
                <X className="h-3 w-3" />
              </span>
            </>
          ) : (
            <>
              <span>{title}</span>
              <ChevronDown className="ms-2 h-4 w-4 opacity-50" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      </TooltipTrigger>
      <PopoverContent
        className={`p-2 ${selectedOnly ? "w-[400px]" : "w-[320px]"}`}
        align="start"
        onCloseAutoFocus={() => {
          // Not prevented: Radix focuses the chip right after this returns
          // (and skips that after a click outside). Only that one focus is
          // swallowed; the flag clears before anything else can run.
          suppressFocusTipRef.current = true
          queueMicrotask(() => {
            suppressFocusTipRef.current = false
          })
        }}
      >
        {showSearch && (
          <div className="flex items-center gap-1.5 mb-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute start-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder={t(selectedOnly ? "shell.filter.searchSelected" : "shell.filter.search", {
                  title: title.toLowerCase(),
                })}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 ps-7 text-sm"
                autoFocus
              />
            </div>
            {!hideSearchMode && <SearchModeToggle mode={searchMode} onChange={setSearchMode} t={t} />}
          </div>
        )}
        {selectedValues.length > 0 && !singleSelect && (
          <div className="flex items-center justify-between mb-2 px-1">
            <button
              type="button"
              onClick={() => {
                setSelectedOnly(prev => !prev)
                setSearch("")
              }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ListChecks className="h-3.5 w-3.5" />
              {selectedOnly
                ? t("shell.filter.showAll", { count: options.length })
                : t("shell.filter.showSelected", { count: effectiveCount })}
            </button>
            {selectedOnly && (
              <Badge variant="secondary" className="rounded-sm px-1.5 py-0 text-[10px] font-normal">
                {t("shell.filter.reviewMode")}
              </Badge>
            )}
          </div>
        )}
        <div
          ref={attachList}
          onScroll={scrolls ? measureMoreBelow : undefined}
          data-testid="multi-select-options"
          className={cn(
            "space-y-1",
            scrolls && "overflow-auto",
            scrolls && (selectedOnly ? "max-h-[360px]" : "max-h-[260px]"),
          )}
        >
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground px-2 py-3 text-center">{t("shell.filter.noResults")}</p>
          ) : (
            <>
              {/* Select All — not for a single-value facet */}
              {!singleSelect && (
              <div
                className="flex items-center space-x-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer border-b mb-1 pb-1.5"
                onClick={handleSelectAll}
              >
                <Checkbox
                  checked={(() => {
                    // Ignore zero-count / disabled rows here, mirroring
                    // handleSelectAll. Otherwise the checkbox sits at
                    // "indeterminate" forever (the disabled rows can
                    // never be selected) and toggling it appears to do
                    // nothing.
                    const selectable = filtered.filter(
                      o => !o.disabled && o.count !== 0,
                    )
                    if (selectable.length === 0) return false
                    if (selectable.every(o => effectiveSelected.has(o.value))) return true
                    if (selectable.some(o => effectiveSelected.has(o.value))) return "indeterminate"
                    return false
                  })()}
                  onCheckedChange={handleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="text-sm flex-1 font-medium">{t("shell.filter.selectAll")}</span>
              </div>
              )}
              {filtered.map(option => {
                const isDisabled = option.disabled || option.count === 0
                return (
                  <div
                    key={option.value}
                    title={isDisabled ? optionHint : undefined}
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-sm ${
                      isDisabled
                        ? 'opacity-40 cursor-not-allowed'
                        : 'hover:bg-accent cursor-pointer'
                    }`}
                    onClick={() => !isDisabled && handleToggle(option.value)}
                    role={singleSelect ? "option" : undefined}
                    aria-selected={singleSelect ? effectiveSelected.has(option.value) : undefined}
                  >
                    {singleSelect ? (
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0",
                          effectiveSelected.has(option.value) ? "opacity-100" : "opacity-0",
                        )}
                        aria-hidden
                      />
                    ) : (
                    <Checkbox
                      checked={effectiveSelected.has(option.value)}
                      disabled={isDisabled}
                      onCheckedChange={() => !isDisabled && handleToggle(option.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    )}
                    <span className="text-sm flex-1 truncate">{option.label}</span>
                    {option.count !== undefined && (
                      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                        ({option.count.toLocaleString()})
                      </span>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
        {scrolls && moreBelow && (
          <p className="mt-1 px-2 text-[10px] text-muted-foreground" aria-hidden>
            {t("shell.filter.scrollForMore")}
          </p>
        )}
        {selectedValues.length > 0 && (
          <div className="border-t mt-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-8 text-xs"
              onClick={() => onSelectionChange([])}
            >
              {singleSelect ? t("shell.filter.clear") : t("shell.table.clearFilters")}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
    {!open && (selectedValues.length > 0 ? (
      <TooltipContent
        side="bottom"
        align="start"
        collisionPadding={12}
        avoidCollisions
        className={`p-0 ${useTwoColumns ? "max-w-[480px]" : "max-w-[260px]"}`}
      >
        <div className="px-3 py-2 border-b border-border/50">
          <p className="text-xs font-semibold">
            {title}
            <span className="ms-1.5 font-normal text-muted-foreground">
              {t("shell.filter.selectedCount", { count: effectiveCount })}
            </span>
          </p>
        </div>
        <ul
          className={`px-3 py-2 max-h-[320px] overflow-y-auto ${
            useTwoColumns ? "columns-2 gap-x-4" : "space-y-0.5"
          }`}
        >
          {previewLabels.map((label, i) => (
            <li
              key={`${label}-${i}`}
              className={`text-xs truncate ${useTwoColumns ? "break-inside-avoid py-0.5" : ""}`}
            >
              {label}
            </li>
          ))}
        </ul>
        <div className="px-3 py-2 border-t border-primary-foreground/20 text-[11px] flex items-center gap-1.5 bg-primary-foreground/10 rounded-b-md">
          <ListChecks className="h-3.5 w-3.5 shrink-0" />
          {hiddenPreviewCount > 0 ? (
            <span>
              <span className="font-semibold">{t("shell.filter.moreCount", { count: hiddenPreviewCount })}</span>{" "}
              {richText(t("shell.filter.reviewAll"), {
                title: <span className="font-semibold underline underline-offset-2">{title}</span>,
              })}
            </span>
          ) : (
            <span>
              {richText(t("shell.filter.editHint"), {
                title: <span className="font-semibold underline underline-offset-2">{title}</span>,
              })}
            </span>
          )}
        </div>
      </TooltipContent>
    ) : description ? (
      <TooltipContent side="bottom" className="max-w-[300px]">
        {description}
      </TooltipContent>
    ) : null)}
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * Compact segmented toggle that flips the global search mode between
 * "exact" (word-boundary) and "fuzzy" (subsequence).
 */
function SearchModeToggle({
  mode,
  onChange,
  t,
}: {
  mode: SearchMode
  onChange: (next: SearchMode) => void
  t: ShellTranslate
}) {
  const exact = t("shell.filter.exact")
  const fuzzy = t("shell.filter.fuzzy")
  return (
    <TooltipProvider delayDuration={300}>
      <div
        role="group"
        aria-label={t("shell.filter.searchMode")}
        className="flex items-center rounded-md border border-input bg-background p-0.5 shrink-0"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onChange("exact")}
              aria-pressed={mode === "exact"}
              className={`px-2 h-6 text-[10px] font-medium rounded-[4px] transition-colors ${
                mode === "exact"
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {exact}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-xs">
            {richText(t("shell.filter.exactHelp"), { mode: <span className="font-semibold">{exact}</span> })}
            <br />
            {t("shell.filter.exactExample")}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onChange("fuzzy")}
              aria-pressed={mode === "fuzzy"}
              className={`px-2 h-6 text-[10px] font-medium rounded-[4px] transition-colors ${
                mode === "fuzzy"
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {fuzzy}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-xs">
            {richText(t("shell.filter.fuzzyHelp"), { mode: <span className="font-semibold">{fuzzy}</span> })}
            <br />
            {t("shell.filter.fuzzyExample")}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}
